import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Utensils, X } from 'lucide-react';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Sheet } from '../ui/Sheet.js';
import { Field } from '../ui/Field.js';
import { useCalories } from '../stores/calories.js';
import type { Food, FoodLog } from '../api/food.js';
import { errorMessage } from '../lib/errors.js';

interface Props {
  open: boolean;
  onClose: () => void;
  date: string;
  /** Set to edit an existing entry instead of creating one. */
  entry?: FoodLog | null;
}

/**
 * Display-only preview of what the server will compute. The stored value always
 * comes back from the API response — this never becomes the source of truth.
 */
function previewKcal(food: Food, grams: number | null): number | null {
  if (grams !== null) {
    return food.kcalPer100g === null ? null : Math.round((food.kcalPer100g * grams) / 100);
  }
  return food.kcalPerPortion;
}

function num(value: string): number | undefined {
  const n = Number(value.replace(',', '.'));
  return value.trim() === '' || Number.isNaN(n) ? undefined : n;
}

export function FoodLogModal({ open, onClose, date, entry }: Props): React.ReactElement | null {
  const addLog = useCalories((s) => s.addLog);
  const editLog = useCalories((s) => s.editLog);
  const removeLog = useCalories((s) => s.removeLog);
  const searchFoods = useCalories((s) => s.searchFoods);
  const foodsLoaded = useCalories((s) => s.foodsLoaded);
  const loadFoods = useCalories((s) => s.loadFoods);

  const [label, setLabel] = useState('');
  const [grams, setGrams] = useState('');
  const [kcal, setKcal] = useState('');
  const [protein, setProtein] = useState('');
  const [selected, setSelected] = useState<Food | null>(null);
  const [override, setOverride] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const gramsRef = useRef<HTMLInputElement>(null);

  const editing = entry != null;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setHighlightIdx(0);
    setOverride(false);
    setSelected(null);
    setLabel(entry?.label ?? '');
    setGrams(entry?.grams != null ? String(entry.grams) : '');
    setKcal(entry ? String(entry.kcal) : '');
    setProtein(entry?.protein != null ? String(entry.protein) : '');
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, entry]);

  // Séparé de la remise à zéro ci-dessus : `foodsLoaded` change quand le catalogue finit de
  // charger, et un seul effet effaçait alors ce qu'on avait tapé entre-temps (le même défaut
  // que dans `CourseAddModal`).
  useEffect(() => {
    if (open && !foodsLoaded) void loadFoods();
  }, [open, foodsLoaded, loadFoods]);

  if (!open) return null;

  const suggestions = editing || selected ? [] : searchFoods(label).slice(0, 5);
  const gramsNum = num(grams) ?? null;
  const computed = selected && !override ? previewKcal(selected, gramsNum) : null;
  // A food known only per 100 g needs a weight, not a calorie count: asking for
  // kcal there would make the user do the arithmetic the catalogue exists to avoid.
  const gramsRequired =
    selected !== null &&
    !override &&
    selected.kcalPer100g !== null &&
    selected.kcalPerPortion === null;
  const awaitingGrams = gramsRequired && gramsNum === null;
  const needsKcal = !selected || override || (computed === null && !gramsRequired);

  function choose(food: Food): void {
    setSelected(food);
    setLabel(food.name);
    setKcal('');
    // La saisie du nom laisse la place à la puce : le focus passe aux grammes — requis pour un
    // aliment connu aux 100 g, optionnels sinon, et Entrée y valide dans les deux cas.
    setTimeout(() => gramsRef.current?.focus(), 0);
  }

  function reset(): void {
    setSelected(null);
    setOverride(false);
    setLabel('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function submit(): Promise<void> {
    if (submitting) return;
    setError(null);
    const trimmed = label.trim();
    if (!trimmed) return;

    setSubmitting(true);
    try {
      if (editing && entry) {
        await editLog(entry.date, entry.logId, {
          label: trimmed,
          ...(num(kcal) !== undefined ? { kcal: num(kcal) as number } : {}),
          protein: num(protein) ?? null,
          grams: gramsNum,
        });
      } else {
        await addLog({
          ...(selected ? { foodId: selected.foodId } : { label: trimmed }),
          ...(gramsNum !== null ? { grams: gramsNum } : {}),
          ...(num(kcal) !== undefined ? { kcal: num(kcal) as number } : {}),
          ...(num(protein) !== undefined ? { protein: num(protein) as number } : {}),
          ...(override ? { kcalOverride: true } : {}),
          date,
        });
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Erreur'));
    } finally {
      setSubmitting(false);
    }
  }

  async function destroy(): Promise<void> {
    if (!entry) return;
    setSubmitting(true);
    try {
      await removeLog(entry.date, entry.logId);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = suggestions[highlightIdx];
      if (pick) choose(pick);
      else void submit();
    }
  }

  const title = editing ? 'Modifier l’entrée' : 'Ajouter un aliment';

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      closeLabel="Fermer"
      anchor="top"
      initialFocusRef={inputRef}
      footer={
        <>
          {editing && (
            <Button variant="danger" className="mr-auto" onClick={() => void destroy()}>
              Supprimer
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!label.trim() || awaitingGrams}
            loading={submitting}
          >
            {editing ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </>
      }
    >
      <div className="pile pile-serree">
        {selected ? (
          <div className="rangee">
            {/* Une puce allumée : l'éteindre retire la sélection. */}
            <button
              type="button"
              className="puce"
              aria-pressed="true"
              onClick={reset}
              aria-label={`Sélection : ${selected.name} — retirer`}
            >
              <Utensils size={14} aria-hidden />
              {selected.name}
              <X size={14} aria-hidden />
            </button>
          </div>
        ) : (
          <Input
            ref={inputRef}
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              setHighlightIdx(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Comté, salade, pain…"
            aria-label="Aliment"
          />
        )}

        {suggestions.length > 0 && (
          <div role="listbox" aria-label="Suggestions" className="liste">
            {suggestions.map((food, idx) => (
              <button
                key={food.foodId}
                type="button"
                role="option"
                aria-selected={highlightIdx === idx}
                onClick={() => choose(food)}
                onMouseEnter={() => setHighlightIdx(idx)}
                className="ligne ligne-cliquable"
              >
                <Utensils size={16} aria-hidden className="shrink-0 text-muted" />
                <span className="ligne-corps">
                  <span className="ligne-titre">{food.name}</span>
                </span>
                <span className="ligne-repere">
                  {food.kcalPer100g !== null
                    ? `${food.kcalPer100g}/100 g`
                    : `${food.kcalPerPortion} / ${food.portionLabel ?? 'portion'}`}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Field label="Grammes" className="flex-1 min-w-0">
            <Input
              ref={gramsRef}
              value={grams}
              onChange={(e) => setGrams(e.target.value)}
              onKeyDown={onKeyDown}
              inputMode="decimal"
              placeholder={gramsRequired ? 'requis' : ''}
            />
          </Field>
          {needsKcal ? (
            <Field label="kcal" className="flex-1 min-w-0">
              <Input
                value={kcal}
                onChange={(e) => setKcal(e.target.value)}
                onKeyDown={onKeyDown}
                inputMode="decimal"
                placeholder="requis"
              />
            </Field>
          ) : (
            <Field label="kcal" className="flex-1 min-w-0" group>
              {/* Calculé par le catalogue : une saisie désactivée, pas un champ à remplir. */}
              <Input value={computed ?? '—'} readOnly disabled aria-label="Calories calculées" />
            </Field>
          )}
          <Field label="Protéines (g)" className="flex-1 min-w-0">
            <Input
              value={protein}
              onChange={(e) => setProtein(e.target.value)}
              onKeyDown={onKeyDown}
              inputMode="decimal"
            />
          </Field>
        </div>

        {selected && !override && computed !== null && (
          <button
            type="button"
            onClick={() => setOverride(true)}
            className="discret underline self-start"
          >
            Valeur exceptionnelle — saisir les kcal à la main
          </button>
        )}

        <p className="alerte alerte-erreur" role="alert">
          {error}
        </p>
      </div>
    </Sheet>
  );
}
